/**
 * PublicTableViewPage — list page for a public table.
 * Supports Card Grid, List/Feed, Data Table, and Timeline view types.
 * Route: /p/:tableSlug
 * No authentication required.
 *
 * The slug normally comes from the route. The feed shell renders this same
 * page inside its middle column on phones and tablets (/feed/events), where
 * there is no :tableSlug segment to read, so it passes the slug as a prop
 * instead. Nothing else differs: the chrome this page used to sit under came
 * from PublicLayout, so simply not wrapping it in PublicLayout is all the
 * embedding needs.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { usePublicTableList } from '../hooks/usePublicTableView';
import { applyPageSEO } from '../utils/seoHelper';
import { usePublicApp, useCrumbLabel } from '../context/PublicAppContext';
import PublicPageHeader from '../components/public/PublicPageHeader';
import EventsIntro from '../components/public/EventsIntro';
import { EVENTS_PATH } from './feed/feedRoutes';
import PublicSearchBar from '../components/public/PublicSearchBar';
import PublicFilterChips from '../components/public/PublicFilterChips';
import PublicCardGrid from '../components/public/PublicCardGrid';
import PublicListFeed from '../components/public/PublicListFeed';
import PublicDataTable from '../components/public/PublicDataTable';
import PublicTimeline from '../components/public/PublicTimeline';
import PublicCustomList from '../components/public/PublicCustomList';
import PublicRelatedPanel from '../components/public/PublicRelatedPanel';
import PublicLoadingSkeleton from '../components/public/PublicLoadingSkeleton';
import PublicErrorPage from '../components/public/PublicErrorPage';
import ScopedCustomStyle from '../components/public/ScopedCustomStyle';

export default function PublicTableViewPage({ slug }) {
  const { tableSlug: routeSlug } = useParams();
  // Prop first, route second — an embedded render has no route param at all.
  const tableSlug = slug || routeSlug;
  const { theme } = useTheme();
  const { setAppBranding } = usePublicApp();
  const [searchParams] = useSearchParams();
  // ?pk= is passed by the admin preview iframe; forward it to the API call
  const previewKey = searchParams.get('pk') || undefined;

  const [params, setParams] = useState({ page: 1 });
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filterResetKey, setFilterResetKey] = useState(0);

  // The events list, as the greeting below already decides it. Named once so
  // the check-in notice and that greeting cannot drift apart.
  const isEventsList = `/p/${tableSlug}` === EVENTS_PATH;

  const { data, isLoading, isError, error, refetch, isPlaceholderData } = usePublicTableList(tableSlug, {
    ...params,
    ...(sortField ? { sort: sortField, sort_dir: sortDir } : {}),
    ...(previewKey ? { pk: previewKey } : {}),
  });

  useEffect(() => {
    if (data?.view_config?.seo) {
      applyPageSEO(data.view_config.seo, data.app_branding);
    }
  }, [data]);

  // Propagate app-level branding to the layout
  useEffect(() => {
    if (data?.app_branding) {
      setAppBranding(data.app_branding);
    }
  }, [data?.app_branding, setAppBranding]);

  // Prefer the view's configured page title over the humanised table slug in
  // the breadcrumb. Must run before the early returns below.
  useCrumbLabel(data?.view_config?.branding?.page_title);

  // Extract view_type for the loading skeleton even before data arrives.
  // The skeleton needs the view type so it can show the right shape.
  const preloadViewType = data?.view_config?.view_type || null;
  if (isLoading) return <PublicLoadingSkeleton viewType={preloadViewType || 'card'} />;
  if (isError) return <PublicErrorPage error={error} onRetry={refetch} />;

  const { view_config = {}, fields = [], records = [], pagination = {} } = data;
  const {
    view_type, branding = {}, interaction_config = {},
    field_mapping = {}, layout_config = {}, custom_css = '',
  } = view_config;
  const accentColor = theme.primary;
  const scopeId = `arcee-public-view-${tableSlug}`;

  const handleSearch = (q) => setParams((p) => ({ ...p, search: q, page: 1 }));
  const handleClearSearch = () => {
    // Clear search + all active filters + reset filter chip UI
    setParams((p) => {
      const base = Object.fromEntries(
        Object.entries(p).filter(([k]) => !k.startsWith('filter['))
      );
      return { ...base, search: '', page: 1 };
    });
    setFilterResetKey((k) => k + 1);
  };
  const handleFilter = (filters) => {
    setParams((p) => {
      // Replace all filter[*] params to avoid stale values from deselected chips
      const base = Object.fromEntries(
        Object.entries(p).filter(([k]) => !k.startsWith('filter['))
      );
      return { ...base, ...filters, page: 1 };
    });
  };
  const handleSort    = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setParams((p) => ({ ...p, page: 1 }));
  };

  // Derive active state for the "Clear" button
  const hasActiveSearch  = !!(params.search);
  const hasActiveFilters = Object.keys(params).some((k) => k.startsWith('filter['));
  const hasActiveQuery   = hasActiveSearch || hasActiveFilters;

  // "The table holds nothing", as opposed to "your query matched nothing".
  // Only the first justifies hiding the toolbar — see where it is used below.
  //
  // Restricted to the custom-layout view on purpose. That is the only view
  // this change was asked to cover, and it is the only one whose empty panel
  // is the new design; the built-in views keep their existing empty states and
  // their toolbar exactly as they are today.
  const isTrulyEmpty =
    view_type === 'custom' && records.length === 0 && !hasActiveQuery;

  const filterableFields = fields.filter(
    (f) => (interaction_config.filterable_fields || []).includes(f.name)
  );

  const sortableFields = interaction_config.sort_enabled
    ? (interaction_config.sortable_fields?.length
        ? fields.filter((f) => interaction_config.sortable_fields.includes(f.name))
        : fields)
    : [];

  const totalPages  = pagination.total_pages || 1;
  const currentPage = pagination.page || 1;
  const totalCount  = pagination.total || records.length;
  const paginationStyle = interaction_config.pagination_style || 'pages';

  const renderRecords = () => {
    const detailEnabled     = interaction_config.detail_enabled !== false;
    const detailButtonLabel = interaction_config.detail_button_label || '';

    // A custom layout, when the builder configured one AND it is usable. The
    // server returns null for a layout that is missing, soft-deleted, still a
    // draft or empty, so null here means "fall back to the built-in renderer" —
    // the same rule the detail page follows. A misconfigured layout degrades to
    // today's card grid; it never blanks the page.
    if (view_type === 'custom' && view_config.layout) {
      return (
        <PublicCustomList
          layout={view_config.layout}
          records={records}
          related={data.related}
          actionButtons={view_config.action_buttons}
          actionForms={view_config.action_forms}
          columnsPerRow={layout_config.cards_per_row}
          detailEnabled={detailEnabled}
          tableSlug={tableSlug}
          // Lets the empty state tell "nothing published yet" apart from
          // "your search matched nothing", and offer a way out of the latter.
          hasActiveQuery={hasActiveQuery}
          onClearQuery={handleClearSearch}
        />
      );
    }

    switch (view_type) {
      case 'card':
        return <PublicCardGrid records={records} fields={fields} mapping={field_mapping} layoutConfig={layout_config} detailEnabled={detailEnabled} detailButtonLabel={detailButtonLabel} />;
      case 'list':
        return <PublicListFeed records={records} fields={fields} mapping={field_mapping} layoutConfig={layout_config} detailEnabled={detailEnabled} detailButtonLabel={detailButtonLabel} />;
      case 'table':
        return (
          <PublicDataTable
            records={records}
            fields={fields}
            mapping={field_mapping}
            onSort={interaction_config.sort_enabled ? handleSort : null}
            sortField={sortField}
            sortDir={sortDir}
            layoutConfig={layout_config}
            detailEnabled={detailEnabled}
            detailButtonLabel={detailButtonLabel}
          />
        );
      case 'timeline':
        return <PublicTimeline records={records} fields={fields} mapping={field_mapping} layoutConfig={layout_config} detailEnabled={detailEnabled} detailButtonLabel={detailButtonLabel} />;
      default:
        return <PublicCardGrid records={records} fields={fields} mapping={field_mapping} layoutConfig={layout_config} detailEnabled={detailEnabled} detailButtonLabel={detailButtonLabel} />;
    }
  };

  return (
    <div id={scopeId}>
      <ScopedCustomStyle css={custom_css} scopeId={scopeId} />
      <div className="container py-4">
      {/* The events list greets the visitor (guest strip, or "Next up" when
          signed in); every other table keeps the configurable header. */}
      {isEventsList
        ? <EventsIntro />
        : <PublicPageHeader branding={branding} recordCount={branding.show_record_count !== false ? totalCount : null} />}

      {/* ── Unified toolbar: search | filter dropdowns (scrollable) | sort + count + clear ──
          Hidden when the table itself holds nothing: there is nothing to
          search, filter or sort, so the controls are noise above an empty
          panel. It stays visible whenever a query IS active, even at zero
          results — hiding it there would remove the only way to clear the
          query and strand the visitor on a permanently empty page. */}
      {!isTrulyEmpty && (
      <div className="d-flex align-items-center gap-2 mb-3" style={{ minWidth: 0 }}>

        {/* Search bar */}
        {interaction_config.search_enabled && (
          <PublicSearchBar
            onSearch={handleSearch}
            onClear={handleClearSearch}
            placeholder={`Search ${branding.page_title || ''}…`}
            // Same counter the filter chips reset on, bumped by
            // handleClearSearch, so the toolbar's Clear empties the input too.
            resetKey={filterResetKey}
          />
        )}

        {/* Filter dropdowns — horizontally scrollable middle section */}
        {interaction_config.filter_enabled && filterableFields.length > 0 && (
          <div
            style={{
              flex: '1 1 0',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              overflowX: 'auto',
              minWidth: 0,
              scrollbarWidth: 'none',
            }}
          >
            <PublicFilterChips
              key={filterResetKey}
              fields={filterableFields}
              onFilter={handleFilter}
            />
          </div>
        )}

        {/* Clear button — REMOVED.
            It appeared only once a search or filter was active, and appearing
            is exactly the problem: the toolbar is one flex row, so the extra
            chip pushed the sort control and the record count past the right
            edge on a phone and the count scrolled out of sight. The search
            box's own X still clears everything (it calls this same
            handleClearSearch), and filter chips are deselected individually,
            so nothing is unreachable without it.

            Restore by uncommenting the block below.

        {hasActiveQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            style={{
              flexShrink: 0,
              fontSize: '0.79rem', fontWeight: 500,
              borderRadius: 'var(--button-radius)',
              border: `1px solid ${accentColor}40`,
              color: accentColor,
              background: `${accentColor}0c`,
              whiteSpace: 'nowrap',
              padding: '5px 12px',
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              transition: 'background 150ms ease, border-color 150ms ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = `${accentColor}18`;
              e.currentTarget.style.borderColor = accentColor;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = `${accentColor}0c`;
              e.currentTarget.style.borderColor = `${accentColor}40`;
            }}
          >
            <i className="bi bi-x-circle" style={{ fontSize: '0.72rem' }} />
            Clear
          </button>
        )}
        */}

        {/* Right section: sort + record count */}
        <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0, marginLeft: 'auto' }}>

          {/* Sort dropdown — only when sort is enabled and NOT table view */}
          {interaction_config.sort_enabled && view_type !== 'table' && sortableFields.length > 0 && (
            <div className="d-flex align-items-center gap-1">
              <select
                style={{
                  fontSize: '0.8rem',
                  borderRadius: 'var(--button-radius)',
                  border: `1.5px solid ${sortField ? accentColor : '#e2e8f0'}`,
                  color: sortField ? accentColor : '#64748b',
                  fontWeight: sortField ? 600 : 400,
                  background: sortField ? `${accentColor}0c` : '#fff',
                  minWidth: 130,
                  padding: '5px 28px 5px 12px',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'border-color 200ms ease, color 200ms ease, background 200ms ease',
                  appearance: 'auto',
                }}
                value={sortField || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) { setSortField(null); setParams((p) => ({ ...p, page: 1 })); }
                  else handleSort(val);
                }}
              >
                <option value="">Sort by…</option>
                {sortableFields.map((f) => (
                  <option key={f.name} value={f.name}>{f.display_name}</option>
                ))}
              </select>
              {sortField && (
                <button
                  style={{
                    fontSize: '0.82rem',
                    borderRadius: 'var(--button-radius)',
                    padding: '5px 10px',
                    border: `1.5px solid ${accentColor}`,
                    color: accentColor,
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    transition: 'background 150ms ease, color 150ms ease',
                  }}
                  title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'} — click to toggle`}
                  onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  onMouseEnter={e => { e.currentTarget.style.background = accentColor; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = accentColor; }}
                >
                  <i className={`bi bi-sort-${sortDir === 'asc' ? 'up' : 'down'}-alt`} />
                </button>
              )}
            </div>
          )}

          {/* Record count */}
          {totalCount > 0 && (
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary, #94a3b8)', whiteSpace: 'nowrap' }}>
              {totalCount} {totalCount === 1 ? 'Event' : 'Events'}
            </span>
          )}
        </div>
      </div>
      )}

      {/* ── Records ── */}
      {/* Other tables' data, where the builder placed it. Nothing renders
          unless a source was given a placement in Related Data. */}
      <PublicRelatedPanel related={data.related} position="above" accentColor={accentColor} />

      {/* Dimmed while the previous results stand in for a query in flight. */}
      <div style={{ opacity: isPlaceholderData ? 0.55 : 1, transition: 'opacity 150ms ease' }}>
        {renderRecords()}
      </div>

      <PublicRelatedPanel related={data.related} position="below" accentColor={accentColor} />

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
          {paginationStyle === 'load_more' ? (
            currentPage < totalPages && (
              <button
                style={{
                  background: accentColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--button-radius)',
                  padding: '9px 28px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: `0 4px 16px ${accentColor}40`,
                  transition: 'opacity 150ms ease, transform 150ms ease, box-shadow 150ms ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.88';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 6px 20px ${accentColor}50`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${accentColor}40`;
                }}
                onClick={() => setParams((p) => ({ ...p, page: (p.page || 1) + 1 }))}
              >
                <i className="bi bi-plus-circle" />
                Load more
              </button>
            )
          ) : (
            <>
              <button
                disabled={currentPage <= 1}
                onClick={() => setParams((p) => ({ ...p, page: p.page - 1 }))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 16px',
                  fontSize: '0.82rem', fontWeight: 600,
                  border: `1.5px solid ${currentPage <= 1 ? '#e2e8f0' : accentColor}`,
                  borderRadius: 'var(--button-radius)',
                  background: 'transparent',
                  color: currentPage <= 1 ? '#cbd5e1' : accentColor,
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
                onMouseEnter={e => {
                  if (currentPage > 1) {
                    e.currentTarget.style.background = accentColor;
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={e => {
                  if (currentPage > 1) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = accentColor;
                  }
                }}
              >
                <i className="bi bi-chevron-left" style={{ fontSize: '0.72rem' }} />
                Prev
              </button>
              <span style={{
                fontSize: '0.8rem', color: '#94a3b8', whiteSpace: 'nowrap',
                padding: '4px 12px',
                background: '#f8fafc',
                borderRadius: 'var(--button-radius)',
                border: '1px solid #f1f5f9',
              }}>
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setParams((p) => ({ ...p, page: (p.page || 1) + 1 }))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 16px',
                  fontSize: '0.82rem', fontWeight: 600,
                  border: `1.5px solid ${currentPage >= totalPages ? '#e2e8f0' : accentColor}`,
                  borderRadius: 'var(--button-radius)',
                  background: 'transparent',
                  color: currentPage >= totalPages ? '#cbd5e1' : accentColor,
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
                onMouseEnter={e => {
                  if (currentPage < totalPages) {
                    e.currentTarget.style.background = accentColor;
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={e => {
                  if (currentPage < totalPages) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = accentColor;
                  }
                }}
              >
                Next
                <i className="bi bi-chevron-right" style={{ fontSize: '0.72rem' }} />
              </button>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
